import { MapPin, Mail, Globe, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/site/PageHeader";
import { Reveal } from "@/components/site/Reveal";
import { CTASection } from "@/components/site/CTASection";
import { ABOUT_SECTIONS, COMPANY, IMAGES, STATS } from "@/data/content";
import { CountUp } from "@/components/site/CountUp";

const linkify = (text) =>
  text.split(/(www\.[a-z0-9.-]+\.[a-z]{2,})/gi).map((part, i) =>
    /^www\./i.test(part) ? (
      <a
        key={i}
        href={`https://${part}`}
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-saffron underline underline-offset-2 transition-colors hover:text-saffron-soft"
      >
        {part}
      </a>
    ) : (
      part
    )
  );

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHeader
        eyebrow="About Us"
        title="A Legacy Forged In Jodhpur - Proudly Indian"
        subtitle="Fifty-two years of building engines, propellers and aircraft with Indian hands — from a single machine in 1974 to indigenous defence RPAS trusted nationwide."
        image={IMAGES.vintage}
      />

      {/* Intro two-column */}
      <section className="bg-aero-deep">
        <div className="mx-auto grid max-w-[1400px] gap-10 px-5 py-20 sm:px-8 sm:py-28 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal>
            <h2 className="font-heading text-4xl font-bold leading-[1.05] tracking-tight text-ink sm:text-5xl">
              Since 1974, engineered in India, for Indian skies.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="space-y-5 font-body text-base leading-relaxed text-steel">
              <p>
                Sharma Model Aero Engines &amp; Propellers began with a stubborn
                belief that a nation should build its own wings. Half a century
                later, that belief still runs every machine on our floor.
              </p>
              <p>
                What follows is our story — from origin, through growth and
                precision manufacturing, into defence, and onward through the
                family legacy and community that carry us today.
              </p>
              <div className="flex flex-wrap gap-8 pt-4">
                {STATS.slice(0, 3).map((s, i) => (
                  <div key={i}>
                    <div className="font-heading text-3xl font-black text-saffron">
                      <CountUp value={s.value} suffix={s.suffix} decimals={s.decimals || 0} />
                    </div>
                    <div className="mt-1 max-w-[140px] font-body text-xs text-steel">
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Numbered chapters */}
      <section className="bg-aero-surface">
        <div className="mx-auto max-w-[1400px] px-5 py-20 sm:px-8 sm:py-28">
          <div className="space-y-0">
            {ABOUT_SECTIONS.map((sec, i) => (
              <Reveal key={sec.no} delay={0.05}>
                <div className="grid gap-6 border-t border-steel/20 py-10 md:grid-cols-[auto_1fr] md:gap-14">
                  <div className="flex items-start gap-4">
                    <span className="font-heading text-5xl font-black text-stroke sm:text-6xl">
                      {sec.no}
                    </span>
                  </div>
                  <div className="max-w-3xl">
                    <h3 className="font-heading text-2xl font-semibold text-ink sm:text-3xl">
                      {sec.title}
                    </h3>
                    <p className="mt-4 font-body text-base leading-relaxed text-steel">
                      {linkify(sec.body)}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Get in Touch block */}
      <section className="bg-aero-deep" data-testid="about-get-in-touch">
        <div className="mx-auto max-w-[1400px] px-5 py-20 sm:px-8 sm:py-28">
          <Reveal>
            <div className="overflow-hidden rounded-2xl border border-steel/20 bg-aero-navy">
              <div className="grid gap-10 p-8 sm:p-12 lg:grid-cols-2">
                <div>
                  <span className="font-heading text-xs uppercase tracking-[0.28em] text-saffron">
                    Get In Touch
                  </span>
                  <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight text-ink sm:text-4xl">
                    Speak with the makers.
                  </h2>
                  <p className="mt-4 max-w-md font-body text-base leading-relaxed text-steel">
                    Whether you are a hobbyist, a club, an NCC unit or a defence
                    procurement team — we would be glad to help.
                  </p>
                  <Link
                    to="/contact"
                    data-testid="about-contact-btn"
                    className="group mt-8 inline-flex items-center gap-2 rounded-full bg-saffron px-7 py-3.5 font-heading font-semibold text-white transition-all duration-300 hover:bg-saffron-soft"
                  >
                    Contact Us
                    <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                </div>
                <div className="flex flex-col justify-center gap-5 border-t border-steel/20 pt-8 lg:border-l lg:border-t-0 lg:pl-12 lg:pt-0">
                  <div className="flex gap-4">
                    <MapPin className="mt-1 h-5 w-5 shrink-0 text-saffron" />
                    <div>
                      <p className="font-heading text-sm font-semibold text-ink">Visit Us</p>
                      <p className="font-body text-sm text-steel">{COMPANY.address}</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <Mail className="mt-1 h-5 w-5 shrink-0 text-saffron" />
                    <div>
                      <p className="font-heading text-sm font-semibold text-ink">Email Us</p>
                      <a href={`mailto:${COMPANY.email}`} className="font-body text-sm text-steel hover:text-ink">
                        {COMPANY.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <Globe className="mt-1 h-5 w-5 shrink-0 text-saffron" />
                    <div>
                      <p className="font-heading text-sm font-semibold text-ink">Online</p>
                      <p className="font-body text-sm text-steel">{COMPANY.website}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection title="Fifty-two years — a legacy that keeps soaring." />
    </div>
  );
}
